import { StrictMode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";
import { Landing } from "./pages/Landing.js";
import { LegalPage } from "./pages/Legal.js";
import { NotFound } from "./pages/NotFound.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";

// Pathless layout that wraps the whole tree in StrictMode + the top-level
// ErrorBoundary. ErrorBoundary renders its children verbatim (no extra DOM),
// so the prerendered markup is unaffected; <Outlet/> mounts the matched route.
function RootLayout() {
  return (
    <StrictMode>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </StrictMode>
  );
}

// react-router data routes consumed by vite-react-ssg. Only the public
// marketing routes (`/`, `/privacy`, `/terms`) are prerendered to static HTML
// at build time (see `ssgOptions.includedRoutes` in vite.config.ts); everything
// under `/app/*` and the auth pages stay client-rendered (SPA), as before.
// The old standalone /app/account redirects into Settings.
//
// SPA-only routes load through react-router `lazy` so their code splits out
// of the entry chunk: the marketing pages (the only ones where first-paint
// speed is scored) ship without the dashboard/kanban/editor code. Each lazy
// import resolves the page's named export to the route Component.
export const routes: RouteRecord[] = [
  {
    element: <RootLayout />,
    children: [
      { index: true, element: <Landing /> },
      {
        path: "signin",
        lazy: async () => ({ Component: (await import("./pages/Signin.js")).Signin }),
      },
      {
        path: "forgot-password",
        lazy: async () => ({
          Component: (await import("./pages/ForgotPassword.js")).ForgotPassword,
        }),
      },
      {
        path: "reset-password",
        lazy: async () => ({
          Component: (await import("./pages/ResetPassword.js")).ResetPassword,
        }),
      },
      {
        path: "onboard",
        lazy: async () => ({ Component: (await import("./pages/Onboard.js")).Onboard }),
      },
      {
        path: "demo-job",
        lazy: async () => ({ Component: (await import("./pages/DemoJob.js")).DemoJob }),
      },
      { path: "privacy", element: <LegalPage doc="privacy" /> },
      { path: "terms", element: <LegalPage doc="terms" /> },
      {
        path: "app/extension-setup",
        lazy: async () => ({
          Component: (await import("./pages/ExtensionSetup.js")).ExtensionSetup,
        }),
      },
      {
        path: "app",
        lazy: async () => ({
          Component: (await import("./components/AppShell.js")).AppShell,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import("./pages/Dashboard.js")).Dashboard,
            }),
          },
          {
            path: "job/:id",
            lazy: async () => ({
              Component: (await import("./pages/JobDetail.js")).JobDetail,
            }),
          },
          {
            path: "profiles",
            lazy: async () => ({
              Component: (await import("./pages/MyProfiles.js")).MyProfiles,
            }),
          },
          {
            path: "profiles/new",
            lazy: async () => ({
              Component: (await import("./pages/MyProfiles.js")).MyProfiles,
            }),
          },
          {
            path: "credits",
            lazy: async () => ({
              Component: (await import("./pages/Credits.js")).Credits,
            }),
          },
          {
            path: "settings",
            lazy: async () => ({
              Component: (await import("./pages/SettingsLayout.js")).SettingsLayout,
            }),
            children: [
              { index: true, element: <Navigate to="/app/settings/general" replace /> },
              {
                path: "general",
                lazy: async () => ({
                  Component: (await import("./pages/settings/SettingsGeneral.js"))
                    .SettingsGeneral,
                }),
              },
              {
                path: "warps",
                lazy: async () => ({
                  Component: (await import("./pages/settings/SettingsWarps.js"))
                    .SettingsWarps,
                }),
              },
              {
                path: "account",
                lazy: async () => ({
                  Component: (await import("./pages/settings/SettingsAccount.js"))
                    .SettingsAccount,
                }),
              },
              {
                path: "support",
                lazy: async () => ({
                  Component: (await import("./pages/settings/SettingsSupport.js"))
                    .SettingsSupport,
                }),
              },
            ],
          },
          {
            path: "archive",
            lazy: async () => ({
              Component: (await import("./pages/Archive.js")).Archive,
            }),
          },
          { path: "account", element: <Navigate to="/app/settings/account" replace /> },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
];
